import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';

// ─── THEME ────────────────────────────────────────────────────
const NAVY   = 'var(--accent)';
const STEEL  = 'var(--accent)';
const LIGHT  = 'var(--accent-bg)';
const GREEN  = 'var(--success)';
const AMBER  = 'var(--warn)';
// Section/verify-card headers used to get RED/TEAL/PURPLE per section purely
// as a category identifier — no urgency difference between them, so all
// default to NAVY like every other section header (see
// CAERA_DASHBOARD_REDESIGN_SPEC.md §9). Matches NurseAssessmentForm.js.
const RED    = NAVY;
const TEAL   = NAVY;
const PURPLE = NAVY;
const BORDER = 'var(--accent-border)';

// ─── STYLE HELPERS ────────────────────────────────────────────
const card  = { background: '#fff', borderRadius: 10, border: `1px solid ${BORDER}`, marginBottom: 28, overflow: 'hidden', boxShadow: '0 1px 6px rgba(26,58,107,0.07)' };
const mkHeader = (color = NAVY) => ({ background: color, color: '#fff', padding: '13px 22px', fontFamily: "'Georgia', serif", fontSize: 15, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 10 });
const subHdr = { background: LIGHT, color: NAVY, padding: '9px 22px', fontFamily: "'Georgia', serif", fontSize: 13, fontWeight: 700, borderBottom: `1px solid ${BORDER}`, borderTop: `1px solid ${BORDER}` };
const lbl = { fontFamily: "'Trebuchet MS', sans-serif", fontSize: 12, fontWeight: 700, color: NAVY, marginBottom: 4, display: 'block' };
const inp = { width: '100%', border: `1.5px solid ${BORDER}`, borderRadius: 6, padding: '8px 11px', fontFamily: "'Trebuchet MS', sans-serif", fontSize: 13, color: 'var(--text)', background: 'var(--bg2)', boxSizing: 'border-box', outline: 'none' };
const txa = { ...inp, minHeight: 80, resize: 'vertical' };
const cbRow = { display: 'flex', flexWrap: 'wrap', gap: '8px 20px', padding: '10px 0' };
const pad = { padding: '18px 22px' };

// ─── PRIMITIVES ───────────────────────────────────────────────
const F = ({ label, children, half, third }) => (
  <div style={{ flex: third ? '1 1 30%' : half ? '1 1 45%' : '1 1 100%', minWidth: 150, marginBottom: 14 }}>
    {label && <label style={lbl}>{label}</label>}
    {children}
  </div>
);
const TI = ({ value, onChange, placeholder, type = 'text', readOnly }) => (
  <input type={type} style={{ ...inp, background: readOnly ? 'var(--bg2)' : 'var(--card-bg)', cursor: readOnly ? 'not-allowed' : 'text' }}
    value={value || ''} onChange={e => !readOnly && onChange(e.target.value)}
    placeholder={placeholder || ''} readOnly={readOnly} />
);
const TA = ({ value, onChange, placeholder, rows }) => (
  <textarea style={{ ...txa, minHeight: rows ? rows * 22 : 80 }}
    value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder || ''} />
);
const Radio = ({ options, value, onChange }) => (
  <div style={cbRow}>
    {options.map(o => (
      <label key={o} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontFamily: "'Trebuchet MS', sans-serif", cursor: 'pointer' }}>
        <input type="radio" checked={value === o} onChange={() => onChange(o)} style={{ accentColor: STEEL }} />{o}
      </label>
    ))}
  </div>
);
const Checks = ({ options, value, onChange }) => (
  <div style={cbRow}>
    {options.map(o => (
      <label key={o} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontFamily: "'Trebuchet MS', sans-serif", cursor: 'pointer' }}>
        <input type="checkbox" checked={(value || []).includes(o)}
          onChange={() => onChange((value || []).includes(o) ? (value || []).filter(v => v !== o) : [...(value || []), o])}
          style={{ accentColor: STEEL }} />{o}
      </label>
    ))}
  </div>
);
const Toggle = ({ label, value, onChange, tc = GREEN }) => (
  <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 13, fontFamily: "'Trebuchet MS', sans-serif" }}>
    <div onClick={() => onChange(!value)}
      style={{ width: 42, height: 24, borderRadius: 12, background: value ? tc : 'var(--border2)', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
      <div style={{ position: 'absolute', top: 3, left: value ? 21 : 3, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
    </div>
    <span style={{ fontWeight: value ? 700 : 400, color: value ? tc : 'var(--text3)' }}>{label}</span>
  </label>
);
const SHdr = ({ n, title, color }) => (
  <div style={mkHeader(color)}>
    <span style={{ background: 'rgba(255,255,255,0.2)', borderRadius: '50%', width: 26, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 900, flexShrink: 0 }}>{n}</span>
    {title}
  </div>
);
const SubH = ({ title }) => <div style={subHdr}>{title}</div>;

const VerifyCard = ({ title, icon, color, fields, data, onChange, note }) => (
  <div style={{ border: `1.5px solid ${color}30`, borderRadius: 10, overflow: 'hidden', marginBottom: 18 }}>
    <div style={{ background: `${color}10`, borderBottom: `1px solid ${color}25`, padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
      <span style={{ fontSize: 20 }}>{icon}</span>
      <div style={{ flex: 1 }}>
        <div style={{ fontFamily: "'Georgia', serif", fontSize: 13, fontWeight: 700, color: NAVY }}>{title}</div>
        <div style={{ fontSize: 11, color: 'var(--text3)' }}>{note || 'Pre-filled from patient profile — verify and correct as needed'}</div>
      </div>
      <div style={{ background: `${color}20`, border: `1px solid ${color}40`, borderRadius: 5, padding: '3px 9px', fontSize: 10, fontWeight: 700, color, whiteSpace: 'nowrap' }}>VERIFY</div>
    </div>
    <div style={{ padding: '14px 16px' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
        {fields.map(({ key, label, placeholder, half, third, type, readOnly }) => (
          <div key={key} style={{ flex: third ? '1 1 30%' : half ? '1 1 45%' : '1 1 100%', minWidth: 150 }}>
            <label style={lbl}>{label}</label>
            <TI type={type || 'text'} value={data[key]} onChange={v => onChange(key, v)}
              placeholder={placeholder || ''} readOnly={readOnly} />
          </div>
        ))}
      </div>
    </div>
  </div>
);

const TableSec = ({ columns, data, onChange, addRow }) => (
  <div style={{ overflowX: 'auto', padding: '0 22px 16px' }}>
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
      <thead><tr>{columns.map(c => (
        <th key={c} style={{ background: LIGHT, color: NAVY, padding: '8px 10px', border: `1px solid ${BORDER}`, fontWeight: 700, textAlign: 'left', fontSize: 11 }}>{c}</th>
      ))}</tr></thead>
      <tbody>{data.map((row, i) => (
        <tr key={i}>{columns.map((c, j) => (
          <td key={c} style={{ border: `1px solid ${BORDER}`, padding: 4 }}>
            <textarea value={row[j] || ''} onChange={e => onChange(i, j, e.target.value)}
              style={{ width: '100%', border: 'none', background: 'transparent', fontSize: 12, resize: 'vertical', minHeight: 50, outline: 'none', padding: 4, boxSizing: 'border-box', fontFamily: "'Trebuchet MS', sans-serif" }} />
          </td>
        ))}</tr>
      ))}</tbody>
    </table>
    <button onClick={addRow} style={{ marginTop: 8, background: 'none', border: `1px dashed ${STEEL}`, borderRadius: 5, color: STEEL, padding: '5px 14px', cursor: 'pointer', fontSize: 12, fontFamily: "'Trebuchet MS', sans-serif" }}>+ Add Row</button>
  </div>
);

const ProgBar = ({ step, total }) => (
  <div style={{ display: 'flex', gap: 6, marginBottom: 24 }}>
    {Array.from({ length: total }).map((_, i) => (
      <div key={i} style={{ flex: 1, height: 5, borderRadius: 3, background: i < step ? STEEL : BORDER, transition: 'background 0.3s' }} />
    ))}
  </div>
);

const emptyRows = (n, cols = 4) => Array.from({ length: n }, () => Array(cols).fill(''));

const notice = (color, bg, icon, text) => (
  <div style={{ background: bg, border: `1px solid ${color}`, borderRadius: 6, padding: '8px 12px', marginBottom: 14, fontSize: 11, color, display: 'flex', gap: 8 }}>
    <span>{icon}</span><span dangerouslySetInnerHTML={{ __html: text }} />
  </div>
);

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;

// ─── MAIN COMPONENT ───────────────────────────────────────────
export default function NurseFormPublic() {
  const { token } = useParams();

  const [tokenStatus, setTokenStatus] = useState('loading'); // loading | valid | invalid | expired | used | submitted
  const [nurseName, setNurseName]     = useState('');
  const [expiresAt, setExpiresAt]     = useState('');
  const [saving, setSaving]           = useState(false);
  const [saveStatus, setSaveStatus]   = useState(null);
  const [saveError, setSaveError]     = useState('');
  const [activeSection, setActiveSection] = useState(0);
  const [autoSaveAt, setAutoSaveAt]   = useState(null);
  const serverLoadedRef = useRef(false);
  const autoSaveTimer   = useRef(null);

  const [clientStrId, setClientStrId] = useState('');

  // ── Tab 0: Patient Info ──────────────────────────────────────
  const [naDate, setNaDate]           = useState('');
  const [iepProvided, setIepProvided] = useState('');
  const [patientInfo, setPatientInfo] = useState({ firstName: '', lastName: '', dob: '', age: '', gender: '', language: '', address: '', city: '', county: '', state: 'GA', zip: '', medicaidId: '' });
  const updPatient = (k, v) => setPatientInfo(p => ({ ...p, [k]: v }));
  const [guardian, setGuardian] = useState({ firstName: '', lastName: '', relationship: '', phone: '', altPhone: '', email: '', livesWithChild: '' });
  const updGuardian = (k, v) => setGuardian(p => ({ ...p, [k]: v }));
  const [emergency, setEmergency] = useState({ firstName: '', lastName: '', relationship: '', phone: '', altPhone: '', email: '' });
  const updEmergency = (k, v) => setEmergency(p => ({ ...p, [k]: v }));
  const [pcp, setPcp] = useState({ name: '', practice: '', address: '', phone: '', fax: '', npi: '' });
  const updPcp = (k, v) => setPcp(p => ({ ...p, [k]: v }));
  const [op, setOp] = useState({ name: '', specialty: '', practice: '', address: '', phone: '', fax: '', npi: '' });
  const updOp = (k, v) => setOp(p => ({ ...p, [k]: v }));

  // ── Tab 1: Clinical ──────────────────────────────────────────
  const [primaryDx, setPrimaryDx]     = useState('');
  const [secondaryDx, setSecondaryDx] = useState('');
  const [weight, setWeight]           = useState('');
  const [diet, setDiet]               = useState('');
  const [allergies, setAllergies]     = useState('');
  const [hasAllergies, setHasAllergies] = useState(false);
  const [medications, setMedications] = useState('');
  const [hospitalizations, setHosp]   = useState('');
  const [seizures, setSeizures]       = useState(false);
  const [gTube, setGTube]             = useState(false);
  const [usesOxygen, setUsesOxygen]   = useState(false);
  const [pottyTrained, setPottyTrained] = useState(false);
  const [wearsDiapers, setWearsDiapers] = useState(false);
  const [contractures, setContractures] = useState(false);
  const [swallowing, setSwallowing]   = useState(false);
  const [equipment, setEquipment]     = useState([]);
  const [otherEquip, setOtherEquip]   = useState('');
  const [equipNotes, setEquipNotes]   = useState('');
  const [feedingMethod, setFeedingMethod] = useState([]);
  const [feedingTol, setFeedingTol]   = useState('');
  const [feedingNotes, setFeedingNotes] = useState('');
  const [depLevel, setDepLevel]       = useState('');
  const [adlSupport, setAdlSupport]   = useState([]);
  const [funcNotes, setFuncNotes]     = useState('');
  const [respPattern, setRespPattern] = useState('');
  const [airwayNeeds, setAirwayNeeds] = useState([]);
  const [respNotes, setRespNotes]     = useState('');
  const [neuroDx, setNeuroDx]         = useState([]);
  const [neuroNotes, setNeuroNotes]   = useState('');
  const [skinIntegrity, setSkinIntegrity] = useState('');
  const [guStatus, setGuStatus]       = useState('');
  const [skinNotes, setSkinNotes]     = useState('');
  const [behavFlags, setBehavFlags]   = useState([]);
  const [behavNotes, setBehavNotes]   = useState('');
  const [cgAbility, setCgAbility]     = useState('');
  const [cgAvail, setCgAvail]         = useState('');
  const [cgNotes, setCgNotes]         = useState('');
  const [medNarrative, setMedNarrative] = useState('');
  const [skilledNeeds, setSkilledNeeds] = useState('');
  const [unskilledNeeds, setUnskilledNeeds] = useState('');

  // ── Tab 2: Care Plan ─────────────────────────────────────────
  const [skilledInterv, setSkilledInterv]   = useState([]);
  const [skilledTable, setSkilledTable]     = useState(emptyRows(5));
  const [adlSupport2, setAdlSupport2]       = useState([]);
  const [supervNeeds, setSupervNeeds]       = useState([]);
  const [unskilledTable, setUnskilledTable] = useState(emptyRows(5));
  const [skilledHrs, setSkilledHrs]         = useState('');
  const [unskilledHrs, setUnskilledHrs]     = useState('');
  const [hrsJustification, setHrsJust]      = useState('');
  const [safetyRisks, setSafetyRisks]       = useState([]);
  const [preventive, setPreventive]         = useState('');
  const [emergProtocol, setEmergProtocol]   = useState('');

  // ── Tab 3: Caregiver Training ────────────────────────────────
  const [competencyTable, setCompetencyTable] = useState(emptyRows(5));
  const [teachingTable, setTeachingTable]     = useState(emptyRows(5));

  const updTable = setter => (i, j, val) =>
    setter(prev => prev.map((row, ri) => ri === i ? row.map((cell, ci) => ci === j ? val : cell) : row));
  const addRow = (setter, cols = 4) => () => setter(prev => [...prev, Array(cols).fill('')]);

  // ─── LOAD DATA VIA TOKEN ──────────────────────────────────────
  useEffect(() => {
    if (!token) { setTokenStatus('invalid'); return; }

    const load = async () => {
      try {
        const res = await fetch(`${SUPABASE_URL}/functions/v1/get-nurse-form-data`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });
        const data = await res.json();

        if (!res.ok || !data.success) {
          setTokenStatus(data.error === 'used' ? 'used' : data.error === 'expired' ? 'expired' : 'invalid');
          return;
        }

        const { client, existingAssessment, nurseName: nName, expiresAt: exp } = data;
        setNurseName(nName || '');
        setExpiresAt(exp || '');
        setClientStrId(client.client_id);

        setPatientInfo({
          firstName:  client.first_name  || '',
          lastName:   client.last_name   || '',
          dob:        client.date_of_birth || '',
          age:        client.date_of_birth
            ? String(Math.floor((Date.now() - new Date(client.date_of_birth)) / 31557600000))
            : '',
          gender:     client.gender      || '',
          language:   client.language    || '',
          address:    client.address     || '',
          city:       client.city        || '',
          county:     client.county      || '',
          state:      client.state       || 'GA',
          zip:        client.zip         || '',
          medicaidId: client.medicaid_id || '',
        });
        setGuardian({
          firstName:      client.guardian_first_name   || '',
          lastName:       client.guardian_last_name     || '',
          relationship:   client.guardian_relationship || '',
          phone:          client.guardian_phone        || '',
          altPhone:       client.guardian_alt_phone    || '',
          email:          client.guardian_email        || '',
          livesWithChild: client.guardian_lives_with_child || '',
        });
        setEmergency({
          firstName:    client.emergency_first_name   || '',
          lastName:     client.emergency_last_name     || '',
          relationship: client.emergency_relationship || '',
          phone:        client.emergency_phone        || '',
          altPhone:     client.emergency_alt_phone    || '',
          email:        client.emergency_email        || '',
        });
        setPcp({
          name:     client.doctor_name     || '',
          practice: client.doctor_practice || '',
          address:  client.doctor_address  || '',
          phone:    client.doctor_phone    || '',
          fax:      client.doctor_fax      || '',
          npi:      client.doctor_npi      || '',
        });
        setOp({
          name:      client.ordering_physician_name      || '',
          specialty: client.ordering_physician_specialty || '',
          practice:  client.ordering_physician_practice  || '',
          address:   client.ordering_physician_address   || '',
          phone:     client.ordering_physician_phone     || '',
          fax:       client.ordering_physician_fax       || '',
          npi:       client.ordering_physician_npi       || '',
        });

        const dxParts = (client.full_diagnoses || '').split(' | ');
        setPrimaryDx(dxParts[0] || '');
        setSecondaryDx(dxParts.slice(1).join(' | ') || '');
        setWeight(client.weight_lbs ? String(client.weight_lbs) : '');
        setDiet(client.diet_description || '');
        setAllergies(client.allergies || '');
        setHasAllergies(!!client.has_allergies);
        setMedications(client.current_medications || '');
        setHosp(client.hospitalizations || '');
        setSeizures(!!client.seizures);
        setGTube(!!client.g_tube);
        setUsesOxygen(!!client.uses_oxygen);
        setPottyTrained(!!client.potty_trained);
        setWearsDiapers(!!client.diapers);
        setContractures(!!client.contractures);
        setSwallowing(!!client.swallowing_issues);
        if (client.medical_equipment && Array.isArray(client.medical_equipment)) {
          const otherItem = client.medical_equipment.find(e => e.startsWith('Other:'));
          if (otherItem) setOtherEquip(otherItem.replace(/^Other:\s*/, ''));
          setEquipment(client.medical_equipment.filter(e => !e.startsWith('Other:')));
        }
        if (client.nurse_assessment_date) setNaDate(client.nurse_assessment_date);

        if (existingAssessment) {
          const ea = existingAssessment;
          if (ea.assessment_date) setNaDate(ea.assessment_date);
          setFeedingMethod(ea.feeding_method || []);
          setFeedingTol(ea.feeding_tolerance || '');
          setFeedingNotes(ea.feeding_notes || '');
          setDepLevel(ea.dependency_level || '');
          setAdlSupport(ea.adl_support_needed || []);
          setFuncNotes(ea.functional_notes || '');
          setRespPattern(ea.respiratory_pattern || '');
          setAirwayNeeds(ea.airway_needs || []);
          setRespNotes(ea.respiratory_notes || '');
          setNeuroDx(ea.neuro_diagnoses || []);
          setNeuroNotes(ea.neuro_notes || '');
          setSkinIntegrity(ea.skin_integrity || '');
          setGuStatus(ea.gu_status || '');
          setSkinNotes(ea.skin_notes || '');
          setBehavFlags(ea.behavioral_flags || []);
          setBehavNotes(ea.behavioral_notes || '');
          setCgAbility(ea.caregiver_ability || '');
          setCgAvail(ea.caregiver_availability || '');
          setCgNotes(ea.caregiver_notes || '');
          setMedNarrative(ea.med_necessity_narrative || '');
          setSkilledNeeds(ea.skilled_needs_summary || '');
          setUnskilledNeeds(ea.unskilled_needs_summary || '');
          setSkilledInterv(ea.skilled_interventions || []);
          setAdlSupport2(ea.adl_unskilled || []);
          setSupervNeeds(ea.supervision_needs || []);
          setSkilledHrs(ea.skilled_hours_requested ? String(ea.skilled_hours_requested) : '');
          setUnskilledHrs(ea.unskilled_hours_requested ? String(ea.unskilled_hours_requested) : '');
          setHrsJust(ea.hours_justification || '');
          setSafetyRisks(ea.safety_risks || []);
          setPreventive(ea.preventive_strategies || '');
          setEmergProtocol(ea.emergency_protocol || '');
          if (ea.skilled_goals) setSkilledTable(ea.skilled_goals);
          if (ea.unskilled_goals) setUnskilledTable(ea.unskilled_goals);
          if (ea.competency_audit) setCompetencyTable(ea.competency_audit);
          if (ea.teaching_plan) setTeachingTable(ea.teaching_plan);
          // Extract "Other:" from equipment array
          if (ea.equipment && Array.isArray(ea.equipment)) {
            const otherItem = ea.equipment.find(e => e.startsWith('Other:'));
            if (otherItem) setOtherEquip(otherItem.replace(/^Other:\s*/, ''));
            setEquipment(ea.equipment.filter(e => !e.startsWith('Other:')));
          }
        }

        // Overlay any locally-saved draft on top of server data
        try {
          const raw = localStorage.getItem(`nafp_${token}`);
          if (raw) {
            const s = JSON.parse(raw);
            if (s.naDate)           setNaDate(s.naDate);
            if (s.iepProvided)      setIepProvided(s.iepProvided);
            if (s.primaryDx)        setPrimaryDx(s.primaryDx);
            if (s.secondaryDx)      setSecondaryDx(s.secondaryDx);
            if (s.weight)           setWeight(s.weight);
            if (s.diet)             setDiet(s.diet);
            if (s.allergies)        setAllergies(s.allergies);
            if (s.hasAllergies !== undefined) setHasAllergies(s.hasAllergies);
            if (s.medications)      setMedications(s.medications);
            if (s.hospitalizations) setHosp(s.hospitalizations);
            if (s.seizures !== undefined)     setSeizures(s.seizures);
            if (s.gTube !== undefined)        setGTube(s.gTube);
            if (s.usesOxygen !== undefined)   setUsesOxygen(s.usesOxygen);
            if (s.pottyTrained !== undefined) setPottyTrained(s.pottyTrained);
            if (s.wearsDiapers !== undefined) setWearsDiapers(s.wearsDiapers);
            if (s.contractures !== undefined) setContractures(s.contractures);
            if (s.swallowing !== undefined)   setSwallowing(s.swallowing);
            if (s.equipment?.length)          setEquipment(s.equipment);
            if (s.otherEquip)       setOtherEquip(s.otherEquip);
            if (s.equipNotes)       setEquipNotes(s.equipNotes);
            if (s.feedingMethod?.length) setFeedingMethod(s.feedingMethod);
            if (s.feedingTol)       setFeedingTol(s.feedingTol);
            if (s.feedingNotes)     setFeedingNotes(s.feedingNotes);
            if (s.depLevel)         setDepLevel(s.depLevel);
            if (s.adlSupport?.length) setAdlSupport(s.adlSupport);
            if (s.funcNotes)        setFuncNotes(s.funcNotes);
            if (s.respPattern)      setRespPattern(s.respPattern);
            if (s.airwayNeeds?.length) setAirwayNeeds(s.airwayNeeds);
            if (s.respNotes)        setRespNotes(s.respNotes);
            if (s.neuroDx?.length)  setNeuroDx(s.neuroDx);
            if (s.neuroNotes)       setNeuroNotes(s.neuroNotes);
            if (s.skinIntegrity)    setSkinIntegrity(s.skinIntegrity);
            if (s.guStatus)         setGuStatus(s.guStatus);
            if (s.skinNotes)        setSkinNotes(s.skinNotes);
            if (s.behavFlags?.length) setBehavFlags(s.behavFlags);
            if (s.behavNotes)       setBehavNotes(s.behavNotes);
            if (s.cgAbility)        setCgAbility(s.cgAbility);
            if (s.cgAvail)          setCgAvail(s.cgAvail);
            if (s.cgNotes)          setCgNotes(s.cgNotes);
            if (s.medNarrative)     setMedNarrative(s.medNarrative);
            if (s.skilledNeeds)     setSkilledNeeds(s.skilledNeeds);
            if (s.unskilledNeeds)   setUnskilledNeeds(s.unskilledNeeds);
            if (s.skilledInterv?.length) setSkilledInterv(s.skilledInterv);
            if (s.skilledTable)     setSkilledTable(s.skilledTable);
            if (s.adlSupport2?.length) setAdlSupport2(s.adlSupport2);
            if (s.supervNeeds?.length) setSupervNeeds(s.supervNeeds);
            if (s.unskilledTable)   setUnskilledTable(s.unskilledTable);
            if (s.skilledHrs)       setSkilledHrs(s.skilledHrs);
            if (s.unskilledHrs)     setUnskilledHrs(s.unskilledHrs);
            if (s.hrsJustification) setHrsJust(s.hrsJustification);
            if (s.safetyRisks?.length) setSafetyRisks(s.safetyRisks);
            if (s.preventive)       setPreventive(s.preventive);
            if (s.emergProtocol)    setEmergProtocol(s.emergProtocol);
            if (s.competencyTable)  setCompetencyTable(s.competencyTable);
            if (s.teachingTable)    setTeachingTable(s.teachingTable);
          }
        } catch (_) { /* ignore parse errors */ }

        // Allow autosave to start watching after load settles
        setTimeout(() => { serverLoadedRef.current = true; }, 500);
        setTokenStatus('valid');
      } catch (err) {
        console.error('Token load error:', err);
        setTokenStatus('invalid');
      }
    };

    load();
  }, [token]);

  // ─── LOCALSTORAGE AUTOSAVE (3-second debounce) ────────────────
  useEffect(() => {
    if (!serverLoadedRef.current || !token) return;
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => {
      try {
        const snapshot = {
          naDate, iepProvided, primaryDx, secondaryDx, weight, diet, allergies,
          hasAllergies, medications, hospitalizations,
          seizures, gTube, usesOxygen, pottyTrained, wearsDiapers, contractures, swallowing,
          equipment, otherEquip, equipNotes,
          feedingMethod, feedingTol, feedingNotes,
          depLevel, adlSupport, funcNotes,
          respPattern, airwayNeeds, respNotes,
          neuroDx, neuroNotes,
          skinIntegrity, guStatus, skinNotes,
          behavFlags, behavNotes,
          cgAbility, cgAvail, cgNotes,
          medNarrative, skilledNeeds, unskilledNeeds,
          skilledInterv, skilledTable, adlSupport2, supervNeeds, unskilledTable,
          skilledHrs, unskilledHrs, hrsJustification,
          safetyRisks, preventive, emergProtocol,
          competencyTable, teachingTable,
        };
        localStorage.setItem(`nafp_${token}`, JSON.stringify(snapshot));
        setAutoSaveAt(new Date());
      } catch (_) { /* storage full or unavailable — ignore */ }
    }, 3000);
    return () => clearTimeout(autoSaveTimer.current);
  }, [
    token, naDate, iepProvided, primaryDx, secondaryDx, weight, diet, allergies,
    hasAllergies, medications, hospitalizations,
    seizures, gTube, usesOxygen, pottyTrained, wearsDiapers, contractures, swallowing,
    equipment, otherEquip, equipNotes,
    feedingMethod, feedingTol, feedingNotes,
    depLevel, adlSupport, funcNotes,
    respPattern, airwayNeeds, respNotes,
    neuroDx, neuroNotes,
    skinIntegrity, guStatus, skinNotes,
    behavFlags, behavNotes,
    cgAbility, cgAvail, cgNotes,
    medNarrative, skilledNeeds, unskilledNeeds,
    skilledInterv, skilledTable, adlSupport2, supervNeeds, unskilledTable,
    skilledHrs, unskilledHrs, hrsJustification,
    safetyRisks, preventive, emergProtocol,
    competencyTable, teachingTable,
  ]);

  // ─── BUILD PAYLOADS ───────────────────────────────────────────
  const buildPayloads = () => ({
    clientUpdate: {
      first_name:  patientInfo.firstName,
      last_name:   patientInfo.lastName,
      date_of_birth: patientInfo.dob || null,
      gender:      patientInfo.gender,
      language:    patientInfo.language,
      address:     patientInfo.address,
      city:        patientInfo.city,
      county:      patientInfo.county,
      state:       patientInfo.state,
      zip:         patientInfo.zip,
      guardian_first_name:       guardian.firstName,
      guardian_last_name:        guardian.lastName,
      guardian_relationship:     guardian.relationship,
      guardian_phone:            guardian.phone,
      guardian_alt_phone:        guardian.altPhone,
      guardian_email:            guardian.email,
      guardian_lives_with_child: guardian.livesWithChild,
      emergency_first_name:   emergency.firstName,
      emergency_last_name:    emergency.lastName,
      emergency_relationship: emergency.relationship,
      emergency_phone:        emergency.phone,
      emergency_alt_phone:    emergency.altPhone,
      emergency_email:        emergency.email,
      doctor_name:     pcp.name,
      doctor_practice: pcp.practice,
      doctor_address:  pcp.address,
      doctor_phone:    pcp.phone,
      doctor_fax:      pcp.fax,
      doctor_npi:      pcp.npi,
      ordering_physician_name:      op.name,
      ordering_physician_specialty: op.specialty,
      ordering_physician_practice:  op.practice,
      ordering_physician_address:   op.address,
      ordering_physician_phone:     op.phone,
      ordering_physician_fax:       op.fax,
      ordering_physician_npi:       op.npi,
      full_diagnoses:      [primaryDx, secondaryDx].filter(Boolean).join(' | '),
      weight_lbs:          weight ? parseFloat(weight) : null,
      diet_description:    diet,
      allergies:           allergies || null,
      current_medications: medications,
      hospitalizations,
      seizures, g_tube: gTube, uses_oxygen: usesOxygen,
      potty_trained: pottyTrained, diapers: wearsDiapers,
      contractures, swallowing_issues: swallowing, has_allergies: hasAllergies,
      medical_equipment: [...equipment, ...(otherEquip ? [`Other: ${otherEquip}`] : [])],
      iep_provided: iepProvided || null,
    },
    assessmentPayload: {
      assessment_date:        naDate || new Date().toISOString().split('T')[0],
      primary_diagnosis:      primaryDx,
      secondary_diagnoses:    secondaryDx,
      hospitalizations,
      equipment:              [...equipment, ...(otherEquip ? [`Other: ${otherEquip}`] : [])],
      other_equipment:        otherEquip,
      equipment_notes:        equipNotes,
      feeding_method:         feedingMethod,
      feeding_tolerance:      feedingTol,
      feeding_notes:          feedingNotes,
      dependency_level:       depLevel,
      adl_support_needed:     adlSupport,
      functional_notes:       funcNotes,
      respiratory_pattern:    respPattern,
      airway_needs:           airwayNeeds,
      respiratory_notes:      respNotes,
      neuro_diagnoses:        neuroDx,
      neuro_notes:            neuroNotes,
      skin_integrity:         skinIntegrity,
      gu_status:              guStatus,
      skin_notes:             skinNotes,
      behavioral_flags:       behavFlags,
      behavioral_notes:       behavNotes,
      caregiver_ability:      cgAbility,
      caregiver_availability: cgAvail,
      caregiver_notes:        cgNotes,
      med_necessity_narrative: medNarrative,
      skilled_needs_summary:  skilledNeeds,
      unskilled_needs_summary: unskilledNeeds,
      skilled_interventions:    skilledInterv,
      skilled_goals:            skilledTable,
      adl_unskilled:            adlSupport2,
      supervision_needs:        supervNeeds,
      unskilled_goals:          unskilledTable,
      skilled_hours_requested:  skilledHrs ? Number(skilledHrs) : null,
      unskilled_hours_requested: unskilledHrs ? Number(unskilledHrs) : null,
      hours_justification:      hrsJustification,
      safety_risks:             safetyRisks,
      preventive_strategies:    preventive,
      emergency_protocol:       emergProtocol,
      competency_audit:         competencyTable,
      teaching_plan:            teachingTable,
      iep_provided:             iepProvided || null,
    },
  });

  // ─── SAVE / SUBMIT ────────────────────────────────────────────
  const handleAction = async (submitStatus) => {
    setSaving(true);
    setSaveStatus(null);
    setSaveError('');
    try {
      const { clientUpdate, assessmentPayload } = buildPayloads();
      const res = await fetch(`${SUPABASE_URL}/functions/v1/submit-nurse-assessment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, status: submitStatus, clientUpdate, assessmentPayload }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Save failed');

      if (submitStatus === 'complete') {
        try { localStorage.removeItem(`nafp_${token}`); } catch (_) {}
        setTokenStatus('submitted');
      } else {
        setSaveStatus('saved');
      }
    } catch (err) {
      setSaveStatus('error');
      setSaveError(err.message || 'Unknown error');
    } finally {
      setSaving(false);
    }
  };

  // ─── STATUS SCREENS ───────────────────────────────────────────
  if (tokenStatus === 'loading') return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', flexDirection: 'column', gap: 14, background: 'var(--accent-bg)' }}>
      <div style={{ width: 36, height: 36, border: `3px solid ${STEEL}`, borderTop: '3px solid transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <div style={{ fontFamily: "'Trebuchet MS', sans-serif", color: NAVY, fontSize: 14 }}>Loading your assessment form…</div>
    </div>
  );

  if (tokenStatus === 'submitted') return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', flexDirection: 'column', gap: 18, background: 'var(--accent-bg)', padding: 24 }}>
      <div style={{ background: '#fff', borderRadius: 12, border: `1px solid ${BORDER}`, padding: '40px 36px', maxWidth: 480, textAlign: 'center', boxShadow: '0 2px 12px rgba(26,58,107,0.10)' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
        <div style={{ fontFamily: "'Georgia', serif", fontSize: 20, fontWeight: 700, color: NAVY, marginBottom: 10 }}>Assessment Submitted</div>
        <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 13, color: 'var(--text3)', lineHeight: 1.7 }}>
          Thank you{nurseName ? `, ${nurseName}` : ''}. Your assessment for <strong>{patientInfo.firstName} {patientInfo.lastName}</strong> has been submitted successfully.<br /><br />
          The care coordination team at A Heart For Care has been notified. You may close this window.
        </div>
      </div>
    </div>
  );

  const errorScreens = {
    invalid: { icon: '🔗', title: 'Invalid Link', msg: 'This assessment link is not valid. Please contact the care coordinator who sent it to request a new link.' },
    expired: { icon: '⏰', title: 'Link Expired', msg: 'This assessment link has expired (links are valid for 7 days). Please contact the care coordinator who sent it to request a new link.' },
    used:    { icon: '✓',  title: 'Already Submitted', msg: 'This assessment has already been submitted using this link. If you need to make changes, please contact the care coordination team.' },
  };

  if (errorScreens[tokenStatus]) {
    const { icon, title, msg } = errorScreens[tokenStatus];
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: 'var(--accent-bg)', padding: 24 }}>
        <div style={{ background: '#fff', borderRadius: 12, border: `1px solid ${BORDER}`, padding: '40px 36px', maxWidth: 480, textAlign: 'center', boxShadow: '0 2px 12px rgba(26,58,107,0.10)' }}>
          <div style={{ fontSize: 40, marginBottom: 14 }}>{icon}</div>
          <div style={{ fontFamily: "'Georgia', serif", fontSize: 18, fontWeight: 700, color: NAVY, marginBottom: 10 }}>{title}</div>
          <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 13, color: 'var(--text3)', lineHeight: 1.7 }}>{msg}</div>
          <div style={{ marginTop: 20, fontSize: 11, color: 'var(--text3)' }}>A Heart For Care · AHFC CRM</div>
        </div>
      </div>
    );
  }

  const sections = ['Patient Info', '1: Assessment', '2: Care Plan', '3: Caregiver', 'Review & Submit'];

  return (
    <div style={{ background: 'var(--accent-bg)', minHeight: '100vh', fontFamily: "'Trebuchet MS', sans-serif" }}>

      {/* Header */}
      <div style={{ background: NAVY, padding: '14px 24px', display: 'flex', alignItems: 'center', gap: 14, boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
        <img src="/logo192.png" alt="AHFC" style={{ height: 32, opacity: 0.9 }} onError={e => { e.target.style.display = 'none'; }} />
        <div style={{ flex: 1 }}>
          <div style={{ color: '#fff', fontFamily: "'Georgia', serif", fontSize: 15, fontWeight: 700 }}>
            {patientInfo.firstName ? `${patientInfo.firstName} ${patientInfo.lastName} — Nurse Assessment` : 'AHFC Nurse Assessment'}
          </div>
          <div style={{ color: 'var(--accent-border)', fontSize: 11 }}>
            A Heart For Care &nbsp;·&nbsp; GAPP Unified Assessment v3.0
            {expiresAt ? ` &nbsp;·&nbsp; Link expires ${new Date(expiresAt).toLocaleDateString()}` : ''}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {autoSaveAt && (
            <span style={{ color: 'var(--accent-border)', fontSize: 11 }}>
              Autosaved {autoSaveAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          {saveStatus === 'saved' && <span style={{ color: 'var(--on-navy-success)', fontSize: 12, fontWeight: 700 }}>✓ Progress saved</span>}
          {saveStatus === 'error' && <span style={{ color: 'var(--on-navy-danger)', fontSize: 12, maxWidth: 260, textAlign: 'right' }}>⚠ {saveError}</span>}
          <button onClick={() => handleAction('draft')} disabled={saving} style={{
            background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 7,
            color: '#fff', padding: '8px 14px', cursor: saving ? 'default' : 'pointer',
            fontFamily: "'Trebuchet MS', sans-serif", fontSize: 12, fontWeight: 600
          }}>
            {saving ? 'Saving…' : '💾 Save Progress'}
          </button>
        </div>
      </div>

      <div style={{ maxWidth: 920, margin: '0 auto', padding: '20px 14px' }}>

        {notice(AMBER, 'var(--warn-bg)', '👋',
          `Welcome${nurseName ? `, <strong>${nurseName}</strong>` : ''}. This is a secure, one-time assessment link for <strong>${patientInfo.firstName} ${patientInfo.lastName}</strong>. Complete all sections and click <strong>Submit Assessment</strong> on the final tab. Your progress is saved automatically when you click "Save Progress". The link expires after submission.`)}

        {/* Section nav */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
          {sections.map((s, i) => {
            const tabComplete = [
              patientInfo.firstName && patientInfo.lastName && patientInfo.dob && guardian.firstName && guardian.lastName,
              primaryDx && feedingMethod.length > 0 && depLevel && respPattern,
              skilledHrs && unskilledHrs && emergProtocol,
              competencyTable.some(row => row[0]),
              false,
            ];
            const tabHasData = [
              patientInfo.firstName || patientInfo.lastName || guardian.firstName || guardian.lastName,
              primaryDx || feedingMethod.length > 0 || depLevel,
              skilledHrs || unskilledHrs || emergProtocol,
              competencyTable.some(row => row[0]),
              false,
            ];
            const dotColor = i < sections.length - 1
              ? (tabComplete[i] ? GREEN : tabHasData[i] ? AMBER : 'var(--border2)')
              : 'transparent';
            return (
              <button key={i} onClick={() => setActiveSection(i)} style={{
                padding: '8px 14px', borderRadius: 7, border: 'none', cursor: 'pointer',
                background: activeSection === i ? NAVY : LIGHT,
                color: activeSection === i ? '#fff' : NAVY,
                fontFamily: "'Trebuchet MS', sans-serif", fontSize: 12, fontWeight: 700,
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
                {i < sections.length - 1 && (
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: dotColor, flexShrink: 0, display: 'inline-block' }} />
                )}
                {s}
              </button>
            );
          })}
        </div>
        <ProgBar step={activeSection + 1} total={sections.length} />

        {/* ══ TAB 0 — PATIENT INFO ══ */}
        {activeSection === 0 && (
          <div style={card}>
            <SHdr n="👤" title="Patient, Contact & Physician Verification" color={NAVY} />
            <div style={pad}>
              {notice(AMBER, 'var(--warn-bg)', '📋',
                '<strong>Please verify all information below</strong> against documents and the patient/guardian present at the visit. Correct anything that is wrong or missing.')}

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, marginBottom: 8 }}>
                <F label="Assessment Date (NA Date)" half>
                  <input type="date" style={inp} value={naDate} onChange={e => setNaDate(e.target.value)} />
                </F>
                <F label="IEP Provided?" half>
                  <Radio options={['Yes', 'No', 'N/A']} value={iepProvided} onChange={setIepProvided} />
                </F>
              </div>

              <VerifyCard title="Patient Information" icon="🧒" color={NAVY}
                note="Verify child's details against ID or Medicaid card"
                data={patientInfo} onChange={updPatient}
                fields={[
                  { key: 'firstName', label: 'First Name', half: true },
                  { key: 'lastName', label: 'Last Name', half: true },
                  { key: 'dob', label: 'Date of Birth', type: 'date', third: true },
                  { key: 'age', label: 'Age (auto)', third: true, readOnly: true },
                  { key: 'gender', label: 'Gender', third: true },
                  { key: 'language', label: 'Primary Language', half: true },
                  { key: 'medicaidId', label: 'Medicaid ID (verify only)', half: true, readOnly: true },
                  { key: 'address', label: 'Home Address' },
                  { key: 'city', label: 'City', third: true },
                  { key: 'county', label: 'County', third: true },
                  { key: 'zip', label: 'ZIP Code', third: true },
                ]}
              />
              <VerifyCard title="Guardian / Parent" icon="👨‍👩‍👧" color={STEEL}
                note="Verify with the parent/guardian present at the visit"
                data={guardian} onChange={updGuardian}
                fields={[
                  { key: 'firstName', label: 'First Name', half: true },
                  { key: 'lastName', label: 'Last Name', half: true },
                  { key: 'relationship', label: 'Relationship to Child', half: true },
                  { key: 'phone', label: 'Primary Phone', third: true },
                  { key: 'altPhone', label: 'Alternate Phone', third: true },
                  { key: 'email', label: 'Email Address', third: true },
                  { key: 'livesWithChild', label: 'Lives with Child?', half: true },
                ]}
              />
              <VerifyCard title="Emergency Contact" icon="🚨" color={RED}
                note="Confirm who to call in an emergency — must be different from guardian"
                data={emergency} onChange={updEmergency}
                fields={[
                  { key: 'firstName', label: 'First Name', half: true },
                  { key: 'lastName', label: 'Last Name', half: true },
                  { key: 'relationship', label: 'Relationship to Child', half: true },
                  { key: 'phone', label: 'Primary Phone', third: true },
                  { key: 'altPhone', label: 'Alternate Phone', third: true },
                  { key: 'email', label: 'Email Address', third: true },
                ]}
              />
              <VerifyCard title="Primary Care Physician (PCP)" icon="🩺" color={TEAL}
                note="Verify against physician order or Medicaid paperwork"
                data={pcp} onChange={updPcp}
                fields={[
                  { key: 'name', label: 'Physician Name', half: true },
                  { key: 'practice', label: 'Practice / Hospital', half: true },
                  { key: 'address', label: 'Address' },
                  { key: 'phone', label: 'Phone', third: true },
                  { key: 'fax', label: 'Fax', third: true },
                  { key: 'npi', label: 'NPI Number', third: true },
                ]}
              />
              <VerifyCard title="Ordering Physician / Specialist" icon="👨‍⚕️" color={PURPLE}
                note="Verify against the physician order — required for GAPP authorization"
                data={op} onChange={updOp}
                fields={[
                  { key: 'name', label: 'Physician Name', half: true },
                  { key: 'specialty', label: 'Specialty', half: true },
                  { key: 'practice', label: 'Practice / Hospital', half: true },
                  { key: 'address', label: 'Address', half: true },
                  { key: 'phone', label: 'Phone', third: true },
                  { key: 'fax', label: 'Fax', third: true },
                  { key: 'npi', label: 'NPI Number', third: true },
                ]}
              />
            </div>
          </div>
        )}

        {/* ══ TAB 1 — NURSING ASSESSMENT ══ */}
        {activeSection === 1 && (
          <div style={card}>
            <SHdr n="1" title="Section 1 — Nursing Assessment" color={NAVY} />

            <SubH title="1.1  Diagnoses & Vitals" />
            <div style={pad}>
              <F label="Primary Diagnosis (ICD-10, status & devices)"><TA value={primaryDx} onChange={setPrimaryDx} rows={3} placeholder="e.g. Autism Spectrum Disorder F84.0, nonverbal; Epilepsy G40.909" /></F>
              <F label="Secondary Diagnoses"><TA value={secondaryDx} onChange={setSecondaryDx} rows={2} /></F>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14 }}>
                <F label="Weight (lbs)" half><TI type="number" value={weight} onChange={setWeight} placeholder="e.g. 64.4" /></F>
                <F label="Diet / Dietary Restrictions" half><TI value={diet} onChange={setDiet} /></F>
                <F label="Allergies" half><TI value={allergies} onChange={setAllergies} placeholder="None / list allergies" /></F>
                <F label="Current Medications" half><TI value={medications} onChange={setMedications} /></F>
              </div>
              <F label="Most Recent Hospitalizations"><TA value={hospitalizations} onChange={setHosp} rows={2} /></F>
            </div>

            <SubH title="Medical Flags" />
            <div style={pad}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px 32px' }}>
                <Toggle label="Seizures" value={seizures} onChange={setSeizures} tc={RED} />
                <Toggle label="G-Tube" value={gTube} onChange={setGTube} />
                <Toggle label="Uses Oxygen" value={usesOxygen} onChange={setUsesOxygen} />
                <Toggle label="Potty Trained" value={pottyTrained} onChange={setPottyTrained} />
                <Toggle label="Wears Diapers" value={wearsDiapers} onChange={setWearsDiapers} />
                <Toggle label="Contractures" value={contractures} onChange={setContractures} tc={RED} />
                <Toggle label="Swallowing Issues" value={swallowing} onChange={setSwallowing} tc={RED} />
                <Toggle label="Has Allergies" value={hasAllergies} onChange={setHasAllergies} />
              </div>
            </div>

            <SubH title="1.2  Medical Equipment & Technology" />
            <div style={pad}>
              <Checks options={['Oxygen','Suction','Nebulizer','CPAP / BiPAP','Pulse Oximeter','G-Tube / J-Tube','Feeding Pump','Tracheostomy','Ventilator']} value={equipment} onChange={setEquipment} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, marginBottom: 6 }}>
                <label style={{ ...lbl, marginBottom: 0, whiteSpace: 'nowrap' }}>Other (specify):</label>
                <input type="text" style={{ ...inp, maxWidth: 320 }} value={otherEquip} onChange={e => setOtherEquip(e.target.value)} placeholder="e.g. Pulse oximeter monitor" />
              </div>
              <F label="RN Clinical Notes – Equipment"><TA value={equipNotes} onChange={setEquipNotes} /></F>
            </div>

            <SubH title="1.3  Feeding Assessment" />
            <div style={pad}>
              <label style={lbl}>Feeding Method</label>
              <Checks options={['Oral','G-Tube','Pump Feeding','J-Tube','Other']} value={feedingMethod} onChange={setFeedingMethod} />
              <label style={{ ...lbl, marginTop: 10 }}>Tolerance</label>
              <Radio options={['Good','Fair','Poor']} value={feedingTol} onChange={setFeedingTol} />
              <F label="RN Feeding Assessment Notes"><TA value={feedingNotes} onChange={setFeedingNotes} /></F>
            </div>

            <SubH title="1.4  Mobility & ADL Status" />
            <div style={pad}>
              <label style={lbl}>Dependency Level</label>
              <Radio options={['Independent','Requires Assistance','Dependent','Dependent – All ADLs']} value={depLevel} onChange={setDepLevel} />
              <label style={{ ...lbl, marginTop: 10 }}>ADLs Requiring Support</label>
              <Checks options={['Feeding','Bathing','Grooming','Toileting','Dressing','Transfers','Repositioning','Mobility']} value={adlSupport} onChange={setAdlSupport} />
              <F label="RN Functional Assessment Notes"><TA value={funcNotes} onChange={setFuncNotes} /></F>
            </div>

            <SubH title="1.5  Respiratory Assessment" />
            <div style={pad}>
              <label style={lbl}>Respiratory Pattern</label>
              <Radio options={['Normal','Labored','Retractions','Tachypnea']} value={respPattern} onChange={setRespPattern} />
              <label style={{ ...lbl, marginTop: 10 }}>Airway Needs</label>
              <Checks options={['Trach Care','Suctioning','Oxygen','Nebulizer']} value={airwayNeeds} onChange={setAirwayNeeds} />
              <F label="RN Respiratory Assessment Notes"><TA value={respNotes} onChange={setRespNotes} /></F>
            </div>

            <SubH title="1.6  Neurological Assessment" />
            <div style={pad}>
              <Checks options={['Cerebral Palsy','Seizure Disorder / Epilepsy','Traumatic Brain Injury','Neuromuscular Disorder','Global Dev. Delay w/ Neuro Impairment','Hydrocephalus (VP Shunt / ETV)']} value={neuroDx} onChange={setNeuroDx} />
              <F label="RN Neurological Notes (include last seizure date if applicable)"><TA value={neuroNotes} onChange={setNeuroNotes} /></F>
            </div>

            <SubH title="1.7  Skin & GU Assessment" />
            <div style={pad}>
              <label style={lbl}>Skin Integrity</label>
              <Radio options={['Intact','Rash','Breakdown','Wound / Granulation Tissue','Other']} value={skinIntegrity} onChange={setSkinIntegrity} />
              <label style={{ ...lbl, marginTop: 10 }}>GU Status</label>
              <Radio options={['Normal','Incontinent','Other']} value={guStatus} onChange={setGuStatus} />
              <F label="RN Skin / GU Assessment Notes"><TA value={skinNotes} onChange={setSkinNotes} /></F>
            </div>

            <SubH title="1.8  Behavioral & Safety Considerations" />
            <div style={pad}>
              <Checks options={['Requires Redirection','Wandering Risk','Aggression','Self-Injury Risk','Insufficient Supervision Risk']} value={behavFlags} onChange={setBehavFlags} />
              <F label="RN Behavioral & Safety Notes"><TA value={behavNotes} onChange={setBehavNotes} /></F>
            </div>

            <SubH title="1.9  Caregiver Status" />
            <div style={pad}>
              <label style={lbl}>Caregiver Ability</label>
              <Radio options={['Independent','Needs Training','Limited Ability','Unable']} value={cgAbility} onChange={setCgAbility} />
              <label style={{ ...lbl, marginTop: 10 }}>Caregiver Availability</label>
              <Radio options={['Full-Time','Part-Time','Limited','Not Available']} value={cgAvail} onChange={setCgAvail} />
              <F label="Caregiver Notes"><TA value={cgNotes} onChange={setCgNotes} /></F>
            </div>

            <SubH title="1.10  Medical Necessity Summary" />
            <div style={pad}>
              <F label="Medical Necessity Narrative"><TA value={medNarrative} onChange={setMedNarrative} rows={5} placeholder="Clinical narrative supporting medical necessity…" /></F>
              <F label="Identified Skilled Nursing Needs"><TA value={skilledNeeds} onChange={setSkilledNeeds} rows={3} /></F>
              <F label="Identified Unskilled (CNA / PSS) Needs"><TA value={unskilledNeeds} onChange={setUnskilledNeeds} rows={3} /></F>
            </div>
          </div>
        )}

        {/* ══ TAB 2 — CARE PLAN ══ */}
        {activeSection === 2 && (
          <div style={card}>
            <SHdr n="2" title="Section 2 — Nursing Care Plan" color={STEEL} />

            <SubH title="2.1  Skilled Nursing Care Plan" />
            <div style={pad}>
              <label style={lbl}>Skilled Nursing Interventions</label>
              <Checks options={['Medication Administration','Trach Care','Suctioning','Respiratory Monitoring','Seizure / Neuro Monitoring','Tube Feeding / Pump Mgmt','Wound Care','Vital Signs Monitoring','Infection Surveillance','Caregiver Education','IV / Line Mgmt','Other']} value={skilledInterv} onChange={setSkilledInterv} />
            </div>
            <TableSec columns={['Clinical Domain','Measurable Goal','Skilled Intervention & Actions','Frequency / Target']}
              data={skilledTable} onChange={updTable(setSkilledTable)} addRow={addRow(setSkilledTable)} />

            <SubH title="2.2  Unskilled Care Plan (CNA / PSS)" />
            <div style={pad}>
              <label style={lbl}>ADL / IADL Support Required</label>
              <Checks options={['Feeding','Bathing','Grooming','Toileting','Dressing','Transfers','Repositioning','Mobility / Walking','Meal Preparation','Laundry / Cleaning','School / Transport Assist.','Housekeeping','Errand Support','Supply Management','Other']} value={adlSupport2} onChange={setAdlSupport2} />
              <label style={{ ...lbl, marginTop: 12 }}>Supervision Required</label>
              <Checks options={['Safety Supervision','One-to-One Supervision','Overnight Safety Supervision','Frequent Redirection for Safety','Behavioral Support','Supervision – Unsafe Wandering']} value={supervNeeds} onChange={setSupervNeeds} />
            </div>
            <TableSec columns={['Clinical Domain','Measurable Goal','Unskilled Tasks & Actions','Frequency / Target']}
              data={unskilledTable} onChange={updTable(setUnskilledTable)} addRow={addRow(setUnskilledTable)} />

            <SubH title="2.3  Requested Hours & Justification" />
            <div style={pad}>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 14 }}>
                <div style={{ flex: '1 1 180px' }}>
                  <label style={lbl}>RN Skilled Hours / Week</label>
                  <input type="number" style={inp} value={skilledHrs} onChange={e => setSkilledHrs(e.target.value)} placeholder="e.g. 12" />
                </div>
                <div style={{ flex: '1 1 180px' }}>
                  <label style={lbl}>PSS / CNA Hours / Week</label>
                  <input type="number" style={inp} value={unskilledHrs} onChange={e => setUnskilledHrs(e.target.value)} placeholder="e.g. 56" />
                </div>
                <div style={{ flex: '1 1 160px', background: LIGHT, borderRadius: 8, padding: '10px 14px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <div style={{ fontSize: 11, color: NAVY, fontWeight: 700 }}>Total Hours / Week</div>
                  <div style={{ fontSize: 26, fontWeight: 900, color: NAVY }}>{Number(skilledHrs || 0) + Number(unskilledHrs || 0) || '—'}</div>
                </div>
              </div>
              <F label="Hours Justification — Medical Necessity"><TA value={hrsJustification} onChange={setHrsJust} rows={5} /></F>
            </div>

            <SubH title="2.4  Safety Risk & Monitoring Plan" />
            <div style={pad}>
              <label style={lbl}>Risks Identified</label>
              <Checks options={['Airway Compromise','Aspiration','Seizure','Fall','Infection','Behavioral Escalation','Insufficient Supervision','G-Tube Complication','Skin Breakdown','Other']} value={safetyRisks} onChange={setSafetyRisks} />
              <F label="Preventive Strategies & Monitoring Plan"><TA value={preventive} onChange={setPreventive} rows={4} /></F>
              <F label="Emergency Response Protocol"><TA value={emergProtocol} onChange={setEmergProtocol} rows={4} /></F>
            </div>
          </div>
        )}

        {/* ══ TAB 3 — CAREGIVER TRAINING ══ */}
        {activeSection === 3 && (
          <div style={card}>
            <SHdr n="3" title="Section 3 — Caregiver Skills Assessment & Training Plan" color={TEAL} />
            <div style={{ ...pad, paddingBottom: 6 }}>
              <p style={{ fontSize: 12, color: 'var(--text3)', fontStyle: 'italic', margin: 0 }}>
                The Provider RN has assessed the primary caregiver's current ability to perform required care tasks.
              </p>
            </div>
            <SubH title="3.1  Competency Audit" />
            <TableSec columns={['Required Care Task','Clinical Risk / Justification','Caregiver Status (Able / Needs Training)','Status (Completed / Pending)']}
              data={competencyTable} onChange={updTable(setCompetencyTable)} addRow={addRow(setCompetencyTable)} />
            <SubH title="3.2  Caregiver Teaching Plan" />
            <TableSec columns={['Teaching Goal / Objective','Teaching Method & Content','Completion Date','Status (Completed / Pending)']}
              data={teachingTable} onChange={updTable(setTeachingTable)} addRow={addRow(setTeachingTable)} />
          </div>
        )}

        {/* ══ TAB 4 — REVIEW & SUBMIT ══ */}
        {activeSection === 4 && (
          <div style={card}>
            <SHdr n="✓" title="Review & Submit" color={GREEN} />
            <div style={pad}>

              <div style={{ background: LIGHT, borderRadius: 8, padding: 18, marginBottom: 18 }}>
                <div style={{ fontFamily: "'Georgia', serif", fontSize: 14, fontWeight: 700, color: NAVY, marginBottom: 12 }}>Assessment Summary</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 24px', fontSize: 12 }}>
                  <div><span style={{ color: NAVY, fontWeight: 700 }}>Patient:</span> {[patientInfo.firstName, patientInfo.lastName].filter(Boolean).join(' ') || <span style={{ color: 'var(--text3)' }}>—</span>}</div>
                  <div><span style={{ color: NAVY, fontWeight: 700 }}>Guardian:</span> {[guardian.firstName, guardian.lastName].filter(Boolean).join(' ') || <span style={{ color: 'var(--text3)' }}>—</span>}</div>
                  <div><span style={{ color: NAVY, fontWeight: 700 }}>Primary Dx:</span> {primaryDx || <span style={{ color: 'var(--text3)' }}>—</span>}</div>
                  <div><span style={{ color: NAVY, fontWeight: 700 }}>PCP:</span> {pcp.name || <span style={{ color: 'var(--text3)' }}>—</span>}</div>
                  <div><span style={{ color: NAVY, fontWeight: 700 }}>Ordering MD:</span> {op.name || <span style={{ color: 'var(--text3)' }}>—</span>}</div>
                  <div><span style={{ color: NAVY, fontWeight: 700 }}>Dependency:</span> {depLevel || <span style={{ color: 'var(--text3)' }}>—</span>}</div>
                  <div><span style={{ color: NAVY, fontWeight: 700 }}>Skilled Hrs/wk:</span> {skilledHrs || <span style={{ color: 'var(--text3)' }}>—</span>}</div>
                  <div><span style={{ color: NAVY, fontWeight: 700 }}>PSS Hrs/wk:</span> {unskilledHrs || <span style={{ color: 'var(--text3)' }}>—</span>}</div>
                  <div><span style={{ color: NAVY, fontWeight: 700 }}>Seizures:</span> {seizures ? <span style={{ color: RED, fontWeight: 700 }}>Yes</span> : 'No'}</div>
                  <div><span style={{ color: NAVY, fontWeight: 700 }}>Emergency Contact:</span> {[emergency.firstName, emergency.lastName].filter(Boolean).join(' ') || <span style={{ color: 'var(--text3)' }}>—</span>}</div>
                </div>
              </div>

              {notice(AMBER, 'var(--warn-bg)', '📋', `
                <strong>On Submit — the following updates happen automatically:</strong><br/>
                ✓ Patient basic info, contacts, and physician details updated<br/>
                ✓ Diagnosis, vitals, and medical flags updated<br/>
                ✓ Assessment marked Complete in CAERA<br/>
                ✓ Care coordination team notified
              `)}

              {notice(STEEL + '22', 'var(--accent-bg)', 'ℹ️',
                'The AI narrative and document generation are handled by your care coordinator after reviewing your submission. You do not need to do anything else once submitted.')}

              {saveStatus === 'error' && (
                <div style={{ background: 'var(--danger-bg)', border: '1.5px solid var(--danger)', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 12, color: 'var(--danger)' }}>
                  ⚠ Submit failed: {saveError}
                </div>
              )}

              <button onClick={() => handleAction('complete')} disabled={saving} style={{
                width: '100%', padding: '16px 0', background: saving ? 'var(--border2)' : GREEN,
                border: 'none', borderRadius: 8, color: '#fff',
                fontWeight: 700, fontSize: 15, cursor: saving ? 'default' : 'pointer',
                fontFamily: "'Trebuchet MS', sans-serif", marginBottom: 10
              }}>
                {saving ? '⏳ Submitting…' : '✅ Submit Assessment'}
              </button>

              <div style={{ fontSize: 11, color: 'var(--text3)', textAlign: 'center' }}>
                AHFC GAPP Unified Assessment v3.0 · Submitted assessments cannot be changed via this link · Valid 1 year from NA Date
              </div>
            </div>
          </div>
        )}

        {/* Prev / Next */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
          <button onClick={() => setActiveSection(s => Math.max(0, s - 1))} disabled={activeSection === 0}
            style={{ padding: '10px 24px', borderRadius: 7, border: `1.5px solid ${BORDER}`, background: '#fff', color: activeSection === 0 ? 'var(--border2)' : NAVY, fontWeight: 700, fontSize: 13, cursor: activeSection === 0 ? 'default' : 'pointer', fontFamily: "'Trebuchet MS', sans-serif" }}>
            ← Previous
          </button>
          <button onClick={() => setActiveSection(s => Math.min(sections.length - 1, s + 1))} disabled={activeSection === sections.length - 1}
            style={{ padding: '10px 24px', borderRadius: 7, border: 'none', background: activeSection === sections.length - 1 ? 'var(--border2)' : NAVY, color: '#fff', fontWeight: 700, fontSize: 13, cursor: activeSection === sections.length - 1 ? 'default' : 'pointer', fontFamily: "'Trebuchet MS', sans-serif" }}>
            Next →
          </button>
        </div>
      </div>
    </div>
  );
}
